import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabaseClient';
import { deleteAllCachedData } from '@/utils/taskCache';
import { useAppStore } from '@/store/appStore';
import { useSidebarStore } from '@/store/sidebarStore';
import { handleError } from '@/utils/errorHandler';
import { SESSION_CHECK_INTERVAL_MS } from '@/constants/timings';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
  signInWithGoogle: () => Promise<void>;
  /**
   * Login con email e password. Disponibile solo in sviluppo: in produzione
   * l'unico ingresso è Google OAuth. Serve perché Supabase in locale non ha
   * OAuth configurato, e senza questo si entrerebbe solo iniettando a mano un
   * token nell'URL.
   */
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  useEffect(() => {
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      })
      .catch((err) => {
        handleError(err, { level: 'error', message: 'Errore nel recupero della sessione' });
        setError('Impossibile recuperare la sessione. Riprova.');
        setLoading(false);
      });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      switch (event) {
        case 'SIGNED_OUT':
          setUser(null);
          setSession(null);
          // Force redirect to login - covers revoked tokens and admin actions
          // Don't redirect if already on a public page
          if (!['/login', '/download'].includes(window.location.pathname)) {
            window.location.href = '/login';
          }
          break;
        case 'TOKEN_REFRESHED':
          if (!session) {
            // Refresh token was revoked/expired server-side
            handleError(new Error('Token refresh failed — session is null'), { level: 'warning', message: 'Token refresh failed, signing out', operation: 'authStateChange' });
            supabase.auth.signOut();
          } else {
            setError(null);
          }
          break;
        case 'USER_UPDATED':
          if (session?.user) {
            setUser(session.user);
          }
          break;
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Periodic session validation — detect server-side revocation
  useEffect(() => {
    let consecutiveFailures = 0;

    const id = setInterval(async () => {
      try {
        const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !currentSession) {
          consecutiveFailures++;
          handleError(sessionError || new Error('Session invalid'), { level: 'warning', message: `Periodic session check failed (${consecutiveFailures} consecutive)`, operation: 'sessionPeriodicCheck' });
          if (consecutiveFailures >= 2) {
            supabase.auth.signOut();
          }
        } else {
          consecutiveFailures = 0;
        }
      } catch (err) {
        consecutiveFailures++;
        handleError(err, { level: 'warning', message: `Errore nel controllo periodico della sessione (${consecutiveFailures} consecutive)` });
        if (consecutiveFailures >= 2) {
          supabase.auth.signOut();
        }
      }
    }, SESSION_CHECK_INTERVAL_MS);

    return () => clearInterval(id);
  }, []);

  const signInWithGoogle = useCallback(async () => {
    try {
      setError(null);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/auth/callback',
        },
      });
      if (error) {
        setError(error.message);
      }
    } catch (err) {
      handleError(err, { level: 'error', message: 'Errore nel login con Google' });
      setError('Errore durante il login con Google. Riprova.');
    }
  }, []);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    if (!import.meta.env.DEV) {
      setError('Il login con password è disponibile solo in sviluppo.');
      return;
    }
    try {
      setError(null);
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        setError(error.message);
      }
    } catch (err) {
      handleError(err, { level: 'error', message: 'Errore nel login con password' });
      setError('Errore durante il login. Riprova.');
    }
  }, []);

  const signOut = useCallback(async () => {
    setError(null);

    // 1. Remove all real-time channels
    try {
      supabase.removeAllChannels();
    } catch (e) {
      handleError(e, { level: 'warning', message: 'Errore nella rimozione dei canali realtime' });
    }

    // 2. Delete entire IndexedDB database (all stores: tasks, structure, spaceItems, etc.)
    try {
      await deleteAllCachedData();
    } catch (e) {
      handleError(e, { level: 'warning', message: 'Errore nella cancellazione del database IndexedDB' });
    }

    // 3. Clear Zustand persisted stores
    try {
      useAppStore.persist.clearStorage();
    } catch (e) {
      handleError(e, { level: 'warning', message: 'Errore nella pulizia dello store app' });
    }
    try {
      useSidebarStore.persist.clearStorage();
    } catch (e) {
      handleError(e, { level: 'warning', message: 'Errore nella pulizia dello store sidebar' });
    }

    // 4. Sign out from Supabase (triggers onAuthStateChange -> SIGNED_OUT -> redirect to /login)
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        handleError(error, { level: 'error', message: 'Errore nel logout da Supabase' });
      }
    } catch (e) {
      handleError(e, { level: 'error', message: 'Errore nel logout' });
    }

    // 5. Clear local state (fallback; SIGNED_OUT handler also does this)
    setUser(null);
    setSession(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, loading, error, signInWithGoogle, signInWithPassword, signOut, clearError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
